import axios from 'axios';

declare global {
  interface Window {
    __APP_ENV__?: {
      API_URL: string;
      APP_NAME: string;
      ENV: string;
    };
  }
}

// Runtime env (injected via public/env.js) → build-time REACT_APP_* → empty string
//
// In development (CRA dev server): prefer REACT_APP_API_URL so the CRA proxy
//   can be used for same-origin cookies.  `.env.development` sets it to empty.
// In production builds (serve / nginx / Cloudflare): prefer window.__APP_ENV__
//   (runtime injection without rebuild) so env.js is the single source of truth.
const isDev = process.env.NODE_ENV === 'development';

const runtimeApiUrl = window.__APP_ENV__?.API_URL;
const buildApiUrl = process.env.REACT_APP_API_URL;
export const API_BASE_URL = isDev
  ? (buildApiUrl !== undefined ? buildApiUrl : runtimeApiUrl || '')
  : (runtimeApiUrl || buildApiUrl || '');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send httpOnly cookies automatically
});

// Response interceptor: on 401, try token refresh via cookie
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

function processQueue(error: any, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  failedQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Never intercept these endpoints — let callers handle responses directly
    const skipUrls = [
      '/api/auth/login',
      '/api/auth/logout',
      '/api/auth/refresh',
      '/api/auth/me',        // fetchMe should silently fail on 401
      '/api/v1/parameters/',  // public endpoint — no auth needed
    ];
    const isAuthEndpoint = skipUrls.some(url => originalRequest.url?.includes(url));

    // Don't redirect if we're already on the login page (avoids infinite reload)
    const alreadyOnLoginPage = window.location.pathname === '/login';

    // Only try refresh on 401 and not already retried
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await api.post('/api/auth/refresh');
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        if (!alreadyOnLoginPage) {
          window.location.href = '/login?reason=expired';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 409 Conflict (concurrent login)
    if (error.response?.status === 409 && !isAuthEndpoint) {
      // Don't redirect — let the caller handle the error display
    }

    return Promise.reject(error);
  }
);

// ── fetchAuth: native fetch wrapper with 401 → refresh → redirect logic ─────
// Use this instead of raw fetch() for authenticated API calls.
// It mirrors the axios interceptor above so native-fetch callers get the
// same session-expiry redirect behaviour.

let isFetchRefreshing = false;
let fetchFailedQueue: Array<{
  resolve: (value: Response) => void;
  reject: (error: unknown) => void;
}> = [];

function processFetchQueue(error: unknown, response: Response | null = null) {
  fetchFailedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(response!);
    }
  });
  fetchFailedQueue = [];
}

const FETCH_SKIP_URLS = [
  '/api/auth/login',
  '/api/auth/logout',
  '/api/auth/refresh',
  '/api/auth/me',
  '/api/v1/parameters/',
];

export async function fetchAuth(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

  const isSkipped = FETCH_SKIP_URLS.some((skip) => url.includes(skip));
  const alreadyOnLoginPage =
    typeof window !== 'undefined' && window.location.pathname === '/login';

  // Merge credentials: 'include' by default
  const mergedInit: RequestInit = { ...init, credentials: init?.credentials ?? 'include' };

  let response = await fetch(input, mergedInit);

  if (response.status === 401 && !isSkipped) {
    if (isFetchRefreshing) {
      // Queue this request while a refresh is in-flight
      return new Promise<Response>((resolve, reject) => {
        fetchFailedQueue.push({ resolve, reject });
      }).then(() => fetchAuth(input, init));
    }

    isFetchRefreshing = true;
    try {
      const refreshRes = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (refreshRes.ok) {
        processFetchQueue(null, refreshRes);
        // Retry original request
        response = await fetch(input, mergedInit);
      } else {
        processFetchQueue(new Error('refresh failed'));
        if (!alreadyOnLoginPage) {
          window.location.href = '/login?reason=expired';
        }
      }
    } catch (refreshError) {
      processFetchQueue(refreshError);
      if (!alreadyOnLoginPage) {
        window.location.href = '/login?reason=expired';
      }
    } finally {
      isFetchRefreshing = false;
    }
  }

  return response;
}

export default api;
